import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Recherche à l'intérieur d'une collection.
 *
 * La recherche Shopify ne sait pas se restreindre à une collection : /search?q=
 * balaie toute la boutique, et « vendor:"X" » n'est pas un filtre mais du texte
 * libre. On charge donc une fois la liste des produits de la collection via
 * /collections/<handle>/products.json et on filtre dans le navigateur.
 *
 * Le chargement est différé au premier caractère tapé : une visite qui ne
 * cherche rien ne paie rien. Ensuite tout est instantané, sans aller-retour.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} panel
 * @extends {Component<Refs>}
 */
class CollectionSearchComponent extends Component {
  requiredRefs = ['input', 'panel'];

  #controller = new AbortController();
  /** @type {Array<{titre: string, cle: string, url: string, img: string|null, prix: string}> | null} */
  #produits = null;
  #chargement = null;
  #index = -1;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    this.refs.input.addEventListener('input', this.#onInput, { signal });
    this.refs.input.addEventListener('keydown', this.#onKeyDown, { signal });
    this.refs.input.addEventListener('focus', this.#onFocus, { signal });
    document.addEventListener('click', this.#onOutsideClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** Sans accents ni casse : « Aznavour » doit répondre à « aznavour ». */
  static #norm(s) {
    return (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  async #charge() {
    if (this.#produits) return this.#produits;
    if (this.#chargement) return this.#chargement;

    const handle = this.dataset.collection;
    this.#chargement = (async () => {
      const out = [];
      // Une collection dépasse rarement 250 produits ; on pagine par sécurité.
      for (let page = 1; page <= 4; page++) {
        const r = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
        if (!r.ok) break;
        const { products = [] } = await r.json();
        for (const p of products) {
          const image = p.images?.[0]?.src ?? null;
          out.push({
            titre: p.title,
            cle: CollectionSearchComponent.#norm(p.title),
            url: `/products/${p.handle}`,
            img: image ? `${image}${image.includes('?') ? '&' : '?'}width=96` : null,
            prix: p.variants?.[0]?.price ?? '',
          });
        }
        if (products.length < 250) break;
      }
      this.#produits = out;
      return out;
    })();

    return this.#chargement;
  }

  #onInput = debounce(async () => {
    const terme = this.refs.input.value.trim();
    this.#index = -1;

    if (terme.length < 2) {
      this.#close();
      return;
    }

    const produits = await this.#charge();
    const mots = CollectionSearchComponent.#norm(terme).split(/\s+/).filter(Boolean);
    const trouves = produits.filter((p) => mots.every((m) => p.cle.includes(m)));
    this.#render(trouves, terme);
  }, 150);

  #onFocus = () => {
    if (this.refs.panel.children.length && this.refs.input.value.trim().length >= 2) {
      this.refs.panel.hidden = false;
    }
  };

  #onOutsideClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.#close();
  };

  #close() {
    this.refs.panel.hidden = true;
    this.#index = -1;
  }

  get #items() {
    return /** @type {HTMLAnchorElement[]} */ (
      Array.from(this.refs.panel.querySelectorAll('.collection-search__hit'))
    );
  }

  #highlight(i) {
    const items = this.#items;
    if (!items.length) return;
    this.#index = ((i % items.length) + items.length) % items.length;
    items.forEach((el, n) => {
      el.classList.toggle('is-active', n === this.#index);
      if (n === this.#index) el.scrollIntoView({ block: 'nearest' });
    });
  }

  #onKeyDown = (event) => {
    if (event.key === 'Escape') return this.#close();
    const items = this.#items;
    if (this.refs.panel.hidden || !items.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#highlight(this.#index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#highlight(this.#index - 1);
        break;
      case 'Enter':
        event.preventDefault();
        (items[this.#index >= 0 ? this.#index : 0])?.click();
        break;
    }
  };

  #render(trouves, terme) {
    const { panel } = this.refs;
    panel.textContent = '';

    if (!trouves.length) {
      const vide = document.createElement('p');
      vide.className = 'collection-search__empty';
      vide.textContent = `Aucun album ne correspond à « ${terme} » dans ${this.dataset.titre}.`;
      panel.append(vide);

      const tout = document.createElement('a');
      tout.className = 'collection-search__all';
      tout.href = `${Theme.routes.search_url}?type=product&q=${encodeURIComponent(terme)}`;
      tout.textContent = 'Chercher dans toute la boutique →';
      panel.append(tout);
      panel.hidden = false;
      return;
    }

    const compte = document.createElement('p');
    compte.className = 'collection-search__count';
    compte.textContent =
      trouves.length === 1 ? '1 album trouvé' : `${trouves.length} albums trouvés`;
    panel.append(compte);

    for (const p of trouves.slice(0, 10)) {
      const a = document.createElement('a');
      a.className = 'collection-search__hit';
      a.href = p.url;

      if (p.img) {
        const img = document.createElement('img');
        img.src = p.img;
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        a.append(img);
      }

      const txt = document.createElement('span');
      txt.className = 'collection-search__hit-txt';
      // Le titre est « Artiste · Album » ; dans une collection d'artiste,
      // répéter son nom sur chaque ligne n'apporte rien.
      const sep = p.titre.indexOf('·');
      txt.textContent = sep > 0 ? p.titre.slice(sep + 1).trim() : p.titre;
      a.append(txt);

      if (p.prix) {
        const prix = document.createElement('span');
        prix.className = 'collection-search__hit-price';
        prix.textContent = `${String(p.prix).replace('.', ',')} €`;
        a.append(prix);
      }

      panel.append(a);
    }

    if (trouves.length > 10) {
      const reste = document.createElement('p');
      reste.className = 'collection-search__count';
      reste.textContent = `… et ${trouves.length - 10} autres, précisez votre recherche`;
      panel.append(reste);
    }

    panel.hidden = false;
  }
}

if (!customElements.get('collection-search-component')) {
  customElements.define('collection-search-component', CollectionSearchComponent);
}
